/**
 * OperationalLineageMiniMap
 *
 * Compact overview of the full lineage graph at reduced scale.
 * Shows node positions, selected node highlight, and viewport rectangle.
 */

import React from 'react';
import type { LayoutNode } from './OperationalLineageNodeRenderer.js';
import type { LayoutEdge } from './OperationalLineageEdgeRenderer.js';
import { NEUTRAL } from '../../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface Props {
  layoutNodes: LayoutNode[];
  layoutEdges: LayoutEdge[];
  selectedNodeId?: string | null;
  viewBox: { minX: number; maxX: number; minY: number; maxY: number };
}

const MINIMAP_SIZE = 100;

export const OperationalLineageMiniMap: React.FC<Props> = ({ layoutNodes, layoutEdges, selectedNodeId, viewBox }) => {
  if (layoutNodes.length === 0) return null;

  const scaleX = MINIMAP_SIZE / (viewBox.maxX - viewBox.minX || 1);
  const scaleY = MINIMAP_SIZE / (viewBox.maxY - viewBox.minY || 1);
  const scale = Math.min(scaleX, scaleY);

  const offsetX = -viewBox.minX * scale;
  const offsetY = -viewBox.minY * scale;

  const toMiniX = (x: number) => x * scale + offsetX;
  const toMiniY = (y: number) => y * scale + offsetY;

  return (
    <div
      style={{
        width: MINIMAP_SIZE + 4,
        height: MINIMAP_SIZE + 4,
        backgroundColor: NEUTRAL.bg200,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 3,
        overflow: 'hidden',
        padding: 2,
      }}
      data-testid="lineage-minimap"
    >
      <svg width={MINIMAP_SIZE} height={MINIMAP_SIZE} viewBox={`0 0 ${MINIMAP_SIZE} ${MINIMAP_SIZE}`}>
        {/* Edges */}
        {layoutEdges.map((edge) => (
          <line
            key={edge.id}
            x1={toMiniX(edge.sourceX)}
            y1={toMiniY(edge.sourceY)}
            x2={toMiniX(edge.targetX)}
            y2={toMiniY(edge.targetY)}
            stroke={NEUTRAL.text200}
            strokeWidth={0.5}
            opacity={0.4}
          />
        ))}

        {/* Nodes */}
        {layoutNodes.map((node) => (
          <rect
            key={node.id}
            x={toMiniX(node.x) - 3}
            y={toMiniY(node.y) - 2}
            width={6}
            height={4}
            rx={1}
            fill={node.id === selectedNodeId ? '#d8d8e8' : NEUTRAL.text300}
            opacity={0.8}
          />
        ))}
      </svg>
    </div>
  );
};
